import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Default } from "../../components/default/Default";
import PostRequest from "../../services/PostRequest";

const PAGE_SIZE = 50;

const LEVEL_LABELS = {
  make: "Make", model: "Model", year: "Year",
  series: "Series", engine: "Engine", variant: "Variant",
};

const money = (v) => (v === null || v === undefined ? "—" : `$${Number(v).toFixed(2)}`);

// All colours come from the site's design tokens so light/dark themes both work.
const inputStyle = {
  padding: "8px 10px", border: "1px solid var(--mca-stroke)",
  background: "var(--mca-surface)", color: "var(--mca-text)",
  borderRadius: 8, fontSize: 13, width: "100%",
};

const cardStyle = {
  background: "var(--mca-surface)", border: "1px solid var(--mca-stroke)",
  borderRadius: 12, padding: 12, marginBottom: 12,
};

const errBox = {
  padding: 12, background: "rgba(var(--mca-danger-rgb, 220,38,38), 0.1)",
  color: "var(--mca-danger, #c0392b)", borderRadius: 8, marginBottom: 12, fontSize: 13,
};

const noteBox = {
  padding: 12, background: "var(--mca-surface-2)", color: "var(--mca-muted)",
  borderRadius: 8, marginBottom: 12, fontSize: 13,
};

const tabStyle = (active) => ({
  padding: "8px 16px", border: "none",
  borderBottom: active ? "2px solid var(--mca-primary)" : "2px solid transparent",
  background: "transparent", cursor: "pointer", fontSize: 14,
  fontWeight: active ? 700 : 500,
  color: active ? "var(--mca-text)" : "var(--mca-muted)",
});

const linkBtn = {
  border: "none", background: "transparent", color: "var(--mca-primary)",
  cursor: "pointer", fontSize: 12, fontWeight: 600, padding: 0,
};

const mutedSub = { color: "var(--mca-muted)", fontSize: 12 };
const countLine = { fontSize: 13, color: "var(--mca-muted)", marginBottom: 8 };

export const VehicleFitment = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("vehicle");

  // --- cascade state: ordered [{ level, options, value }] ---
  const [cascade, setCascade] = useState([]);
  const [cascadeErr, setCascadeErr] = useState("");
  const [cascadeBusy, setCascadeBusy] = useState(false);

  // --- resolved vehicle + fitment parts ---
  const [vehicleCode, setVehicleCode] = useState("");
  const [vehicleInfo, setVehicleInfo] = useState(null);
  const [parts, setParts] = useState([]);
  const [partsTotal, setPartsTotal] = useState(0);
  const [partsPages, setPartsPages] = useState(0);
  const [partsPage, setPartsPage] = useState(1);
  const [partsLoading, setPartsLoading] = useState(false);
  const [partsErr, setPartsErr] = useState("");

  // --- reverse lookup (part -> vehicles) ---
  const [revCode, setRevCode] = useState("");
  const [revVehicles, setRevVehicles] = useState([]);
  const [revTotal, setRevTotal] = useState(0);
  const [revPages, setRevPages] = useState(0);
  const [revPage, setRevPage] = useState(1);
  const [revLoading, setRevLoading] = useState(false);
  const [revErr, setRevErr] = useState("");
  const [revSearched, setRevSearched] = useState(false);

  // --- create dialog ---
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createPostcode, setCreatePostcode] = useState("");
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState("");

  // Load the first cascade level (makes) on mount.
  useEffect(() => {
    (async () => {
      try {
        const data = await PostRequest("/schedules/repco/vehicles/cascade/", {});
        setCascade([{ level: data.level, options: data.options || [], value: "" }]);
      } catch (e) {
        setCascadeErr(e.message || "Failed to load vehicle makes");
      }
    })();
  }, []);

  const clearVehicle = () => {
    setVehicleCode(""); setVehicleInfo(null); setParts([]);
    setPartsTotal(0); setPartsPages(0); setPartsErr("");
  };

  const runFitmentSearch = useCallback(async (code, targetPage) => {
    setPartsLoading(true);
    setPartsErr("");
    try {
      const data = await PostRequest("/schedules/repco/fitment/search/", {
        vehicle_code: code, page: targetPage, page_size: PAGE_SIZE,
      });
      setVehicleInfo(data.vehicle || null);
      setParts(data.parts || []);
      setPartsTotal(data.total || 0);
      setPartsPages(data.total_pages || 0);
      setPartsPage(data.page || targetPage);
    } catch (e) {
      setPartsErr(e.message || "Failed to load parts");
      setParts([]);
    } finally {
      setPartsLoading(false);
    }
  }, []);

  // Pick a value at cascade level `index`: truncate downstream, then either
  // resolve the vehicle (variant leaf) or fetch the next level.
  const pickLevel = async (index, value) => {
    const step = cascade[index];
    const trimmed = cascade.slice(0, index + 1).map((s, i) => (i === index ? { ...s, value } : s));
    clearVehicle();

    if (step.level === "variant") {
      setCascade(trimmed);
      if (value) {
        setVehicleCode(value);
        runFitmentSearch(value, 1);
      }
      return;
    }

    if (!value) {
      setCascade(trimmed);
      return;
    }

    const sel = {};
    trimmed.forEach((s) => {
      if (s.value && s.level !== "variant") sel[s.level] = s.value;
    });

    setCascadeBusy(true);
    setCascadeErr("");
    try {
      const data = await PostRequest("/schedules/repco/vehicles/cascade/", sel);
      setCascade([...trimmed, { level: data.level, options: data.options || [], value: "" }]);
    } catch (e) {
      setCascadeErr(e.message || "Failed to load the next level");
      setCascade(trimmed);
    } finally {
      setCascadeBusy(false);
    }
  };

  const resetCascade = async () => {
    clearVehicle();
    setCascadeErr("");
    try {
      const data = await PostRequest("/schedules/repco/vehicles/cascade/", {});
      setCascade([{ level: data.level, options: data.options || [], value: "" }]);
    } catch (e) {
      setCascadeErr(e.message || "Failed to reset");
    }
  };

  const openScrape = () => {
    if (!vehicleInfo) return;
    const nm = `${vehicleInfo.make} ${vehicleInfo.model} ${vehicleInfo.year_range}`.replace(/\s+/g, " ").trim();
    setCreateName(nm ? `${nm} — fitment` : "");
    setCreatePostcode("");
    setCreateErr("");
    setShowCreate(true);
  };

  const submitScrape = async () => {
    setCreateErr("");
    if (!createPostcode.trim()) { setCreateErr("Enter a Repco postcode you've added."); return; }
    setCreating(true);
    try {
      const res = await PostRequest("/schedules/repco/fitment/create_collection/", {
        vehicle_code: vehicleCode,
        customer_postcode: createPostcode.trim(),
        name: createName.trim(),
      });
      setShowCreate(false);
      navigate(`/collections/${res.collection_id}/records`);
    } catch (e) {
      setCreateErr(e.message || "Failed to create collection");
    } finally {
      setCreating(false);
    }
  };

  // `codeArg` lets callers (cross-links) search without waiting on state.
  const runReverse = async (targetPage, codeArg) => {
    const code = (codeArg !== undefined ? codeArg : revCode).trim();
    if (!code) { setRevErr("Enter a product code."); return; }
    setRevLoading(true); setRevErr(""); setRevSearched(true);
    try {
      const data = await PostRequest("/schedules/repco/fitment/reverse/", {
        product_code: code, page: targetPage, page_size: PAGE_SIZE,
      });
      setRevVehicles(data.vehicles || []);
      setRevTotal(data.total || 0);
      setRevPages(data.total_pages || 0);
      setRevPage(data.page || targetPage);
    } catch (e) {
      setRevErr(e.message || "Failed to look up vehicles");
      setRevVehicles([]);
    } finally {
      setRevLoading(false);
    }
  };

  const jumpToReverse = (code) => {
    setTab("reverse");
    setRevCode(code);
    runReverse(1, code);
  };

  return (
    <Default>
      <div className="mca-content">
        <div className="col-head">
          <div>
            <h1 style={{ fontSize: "26px", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--mca-text)" }}>
              Repco Vehicles &amp; Fitment
            </h1>
            <p style={{ color: "var(--mca-muted)", fontSize: "14px", margin: "6px 0 0" }}>
              Pick a vehicle to see every compatible part, or look up which vehicles a part fits.
              Turn any vehicle into a live scrape of all its parts at your own postcode.
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--mca-stroke)", marginBottom: 16 }}>
          <button type="button" style={tabStyle(tab === "vehicle")} onClick={() => setTab("vehicle")}>
            Shop by vehicle
          </button>
          <button type="button" style={tabStyle(tab === "reverse")} onClick={() => setTab("reverse")}>
            Reverse lookup (part → vehicles)
          </button>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* Shop by vehicle */}
        {/* ---------------------------------------------------------------- */}
        {tab === "vehicle" && (
          <>
            <div style={{ ...cardStyle, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
              {cascade.map((step, i) => (
                <label key={`${step.level}-${i}`} style={{ flex: "1 1 150px", minWidth: 130 }}>
                  <div style={{ fontSize: 11, color: "var(--mca-muted)", marginBottom: 4 }}>
                    {LEVEL_LABELS[step.level] || step.level}
                  </div>
                  <select
                    style={inputStyle}
                    value={step.value}
                    disabled={cascadeBusy}
                    onChange={(e) => pickLevel(i, e.target.value)}
                  >
                    <option value="">{`Select ${(LEVEL_LABELS[step.level] || step.level).toLowerCase()}…`}</option>
                    {step.level === "variant"
                      ? step.options.map((o) => (
                          <option key={o.vehicle_code} value={o.vehicle_code}>
                            {o.variant}{o.year_range ? ` (${o.year_range})` : ""}
                          </option>
                        ))
                      : step.options.map((o) => (
                          <option key={String(o)} value={o}>{o}</option>
                        ))}
                  </select>
                </label>
              ))}
              <button className="mca-btn" type="button" onClick={resetCascade} style={{ marginBottom: 2 }}>
                <span className="mca-btn__label">Reset</span>
              </button>
            </div>

            {cascadeErr && <div style={errBox}>{cascadeErr}</div>}

            {/* Resolved vehicle header + scrape action */}
            {vehicleInfo && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, margin: "4px 0 12px", flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: "var(--mca-text)" }}>
                    {[vehicleInfo.make, vehicleInfo.model, vehicleInfo.series, vehicleInfo.engine].filter(Boolean).join(" ")}
                  </div>
                  <div style={mutedSub}>
                    {[vehicleInfo.variant, vehicleInfo.year_range, `code ${vehicleInfo.vehicle_code}`].filter(Boolean).join(" · ")}
                    {vehicleInfo.swept
                      ? ` · ${partsTotal.toLocaleString()} compatible parts`
                      : " · not yet indexed"}
                  </div>
                </div>
                <button className="mca-btn mca-btn--primary" type="button" onClick={openScrape}>
                  <span className="mca-btn__label">Scrape this vehicle's parts →</span>
                </button>
              </div>
            )}

            {partsErr && <div style={errBox}>{partsErr}</div>}

            {vehicleInfo && !partsLoading && parts.length === 0 && (
              <div style={noteBox}>
                {vehicleInfo.swept
                  ? "No compatible parts were found for this vehicle."
                  : "This vehicle hasn't been indexed by our fitment sweep yet. You can still scrape its parts now — we'll fetch its compatible parts live when you create the collection."}
              </div>
            )}

            {vehicleInfo && (parts.length > 0 || partsLoading) && (
              <>
                <div style={countLine}>
                  {partsLoading ? "Loading…" : `${partsTotal.toLocaleString()} compatible parts`}
                </div>
                <div className="mca-table-wrap">
                  <table className="mca-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>Part</th>
                        <th>Brand</th>
                        <th>Price</th>
                        <th title="Reference freight captured at postcode 4500">Freight @4500</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {parts.map((p) => (
                        <tr key={p.product_code}>
                          <td style={{ width: 56 }}>
                            {p.image ? <img src={p.image} alt="" style={{ width: 40, height: 40, objectFit: "contain" }} /> : null}
                          </td>
                          <td style={{ maxWidth: 360 }}>
                            {p.in_catalog && p.url ? (
                              <a href={p.url} target="_blank" rel="noreferrer" style={{ color: "var(--mca-text)", fontWeight: 600, textDecoration: "none" }}>
                                {p.title || p.product_code}
                              </a>
                            ) : (
                              <span style={{ fontWeight: 600 }}>{p.title || p.product_code}</span>
                            )}
                            <div style={mutedSub}>
                              {p.product_code}
                              {p.universal ? " · universal" : ""}
                              {p.notes ? ` · ${p.notes}` : ""}
                              {!p.in_catalog ? " · not in catalog" : ""}
                            </div>
                          </td>
                          <td>{p.brand || "—"}</td>
                          <td className="cell-strong">{money(p.price_value)}</td>
                          <td>{money(p.freight_value)}</td>
                          <td>
                            <button type="button" onClick={() => jumpToReverse(p.product_code)} style={linkBtn}>
                              vehicles →
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {partsPages > 1 && (
                  <div style={{ display: "flex", gap: 8, justifyContent: "center", alignItems: "center", marginTop: 14 }}>
                    <button className="mca-btn" type="button" disabled={partsPage <= 1} onClick={() => runFitmentSearch(vehicleCode, partsPage - 1)}>Prev</button>
                    <span style={countLine}>Page {partsPage} of {partsPages}</span>
                    <button className="mca-btn" type="button" disabled={partsPage >= partsPages} onClick={() => runFitmentSearch(vehicleCode, partsPage + 1)}>Next</button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Reverse lookup */}
        {/* ---------------------------------------------------------------- */}
        {tab === "reverse" && (
          <>
            <div style={{ ...cardStyle, display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
              <label style={{ flex: "1 1 260px" }}>
                <div style={{ fontSize: 11, color: "var(--mca-muted)", marginBottom: 4 }}>Product code</div>
                <input
                  style={inputStyle}
                  value={revCode}
                  onChange={(e) => setRevCode(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && runReverse(1)}
                  placeholder="e.g. A1547402"
                />
              </label>
              <button className="mca-btn mca-btn--primary" type="button" onClick={() => runReverse(1)} style={{ marginBottom: 2 }}>
                <span className="mca-btn__label">Find vehicles</span>
              </button>
            </div>

            {revErr && <div style={errBox}>{revErr}</div>}

            {revSearched && (
              <div style={countLine}>
                {revLoading ? "Loading…" : `${revTotal.toLocaleString()} vehicles fit ${revCode.trim()}`}
              </div>
            )}

            {revSearched && (revVehicles.length > 0 || revLoading) && (
              <div className="mca-table-wrap">
                <table className="mca-table">
                  <thead>
                    <tr>
                      <th>Make</th>
                      <th>Model</th>
                      <th>Series</th>
                      <th>Engine</th>
                      <th>Variant</th>
                      <th>Years</th>
                    </tr>
                  </thead>
                  <tbody>
                    {revVehicles.map((v) => (
                      <tr key={v.vehicle_code}>
                        <td className="cell-strong">{v.make}</td>
                        <td>{v.model}</td>
                        <td>{v.series || "—"}</td>
                        <td>{v.engine || "—"}</td>
                        <td>{v.variant || "—"}</td>
                        <td>{v.year_range || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {revSearched && !revLoading && revVehicles.length === 0 && !revErr && (
              <div style={noteBox}>
                No vehicles are recorded as fitting this part yet. Fitment is only as complete as the sweep — a
                part appears here once a vehicle that lists it has been indexed.
              </div>
            )}

            {revPages > 1 && (
              <div style={{ display: "flex", gap: 8, justifyContent: "center", alignItems: "center", marginTop: 14 }}>
                <button className="mca-btn" type="button" disabled={revPage <= 1} onClick={() => runReverse(revPage - 1)}>Prev</button>
                <span style={countLine}>Page {revPage} of {revPages}</span>
                <button className="mca-btn" type="button" disabled={revPage >= revPages} onClick={() => runReverse(revPage + 1)}>Next</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Scrape-this-vehicle dialog */}
      {showCreate && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
             onClick={() => !creating && setShowCreate(false)}>
          <div style={{ background: "var(--mca-surface)", border: "1px solid var(--mca-stroke)", borderRadius: 14, padding: 24, width: 440, maxWidth: "90vw" }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px", color: "var(--mca-text)" }}>Scrape this vehicle's parts</h2>
            <p style={{ color: "var(--mca-muted)", fontSize: 13, margin: "0 0 16px" }}>
              {vehicleInfo && vehicleInfo.swept
                ? `All ${partsTotal.toLocaleString()} compatible parts (up to 5,000) will be scraped live at your postcode.`
                : "We'll fetch this vehicle's compatible parts live, then scrape each at your postcode (up to 5,000)."}
            </p>
            <label style={{ display: "block", marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: "var(--mca-muted)", marginBottom: 4 }}>Collection name</div>
              <input style={inputStyle} value={createName} onChange={(e) => setCreateName(e.target.value)} placeholder="e.g. Ford Falcon FG — Brisbane" />
            </label>
            <label style={{ display: "block", marginBottom: 8 }}>
              <div style={{ fontSize: 12, color: "var(--mca-muted)", marginBottom: 4 }}>Customer postcode</div>
              <input style={inputStyle} value={createPostcode} onChange={(e) => setCreatePostcode(e.target.value)} placeholder="e.g. 4500" />
            </label>
            <p style={{ color: "var(--mca-muted)", fontSize: 12, margin: "0 0 16px" }}>
              Must be a Repco postcode you've already added on the Postcodes page.
            </p>
            {createErr && <div style={errBox}>{createErr}</div>}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button className="mca-btn" type="button" onClick={() => setShowCreate(false)} disabled={creating}>Cancel</button>
              <button className="mca-btn mca-btn--primary" type="button" onClick={submitScrape} disabled={creating}>
                <span className="mca-btn__label">{creating ? "Creating…" : "Create & run"}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </Default>
  );
};
